/**
 * IntelligentTeacherSuggestions Component
 * 
 * Task 7: Phase 3 - Intelligent Teacher Suggestions (Week 5-6)
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 4.8, 10.1, 10.2, 10.3, 10.4, 10.6, 15.9, 15.10, 15.11, 15.12, 15.15, 15.16, 15.17
 * 
 * Sidebar component that displays ranked teacher suggestions based on intelligent scoring,
 * subject allocation tracking, resource highlighting, quick assign functionality, and auto-fill features.
 */

import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  LinearProgress,
  Divider,
  Tooltip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  IconButton,
  Avatar,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material';
import {
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  EmojiEvents as TrophyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AutoFixHigh as AutoFixIcon,
  Highlight as HighlightIcon,
  Close as CloseIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useState, useMemo } from 'react';
import type { 
  DayOfWeek, 
  ClassSectionWithRelations, 
  TimetableRecordWithRelations,
  TeacherSuggestion,
  AutoFillAssignment,
} from '@timetable/types';
import { useTeacherSuggestions, useAutoFill } from '../../hooks/useMultiSectionTimetable';
import { useTimetableDesignerStore } from '../../store/timetableDesignerStore';
import { LoadingSpinner } from '../LoadingSpinner';
import { EmptyState } from '../EmptyState';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSubjectsDirect, getTeachersDirect, getRoomsDirect, createTimetableRecordDirect } from '@timetable/api';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../Toast';
import { queryKeys } from '../../lib/queryKeys';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface IntelligentTeacherSuggestionsProps {
  editMode: boolean;
  selectedPeriodSlot: { sectionId: string; periodId: string; day: DayOfWeek } | null;
  subjectId: string | null;
  sections: ClassSectionWithRelations[];
  records: TimetableRecordWithRelations[];
  onTeacherSelect: (teacherId: string) => void;
  onHighlightResource: (type: 'teacher' | 'subject', id: string) => void;
  onClearHighlight: () => void;
  onClearSlot?: () => void;
  onSubjectChange?: (subjectId: string | null) => void;
  onSaved?: () => void;
  classId: string;
  academicYear: string;
}

interface SubjectAllocation {
  subjectId: string;
  subjectName: string;
  colorCode: string;
  periodsToday: number;
  periodsTotal: number;
}

interface AutoFillPreviewItem {
  sectionId: string;
  sectionName: string;
  periodId: string;
  day: string;
  teacherId: string;
  teacherName: string;
  subjectId: string;
  subjectName: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const SidebarContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'editMode',
})<{ editMode: boolean }>(({ theme, editMode }) => ({
  width: editMode ? 320 : 60,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  transition: 'width 0.3s ease',
  position: 'relative',
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.grey[100],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.grey[200],
  },
}));

const ScrollableContent = styled(Box)({
  flex: 1,
  overflowY: 'auto',
});

const TeacherListItem = styled(ListItemButton)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5),
}));

const RankBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'rank',
})<{ rank: number }>(({ theme, rank }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: rank === 1 ? '#FFD700' : rank === 2 ? '#C0C0C0' : rank === 3 ? '#CD7F32' : theme.palette.text.secondary,
  fontWeight: 600,
}));

const ScoreBadge = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
}));

const WorkloadBar = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const SubjectListItem = styled(ListItem)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(0.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 2),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// ============================================================================
// Main Component
// ============================================================================

export function IntelligentTeacherSuggestions({
  editMode,
  selectedPeriodSlot,
  sections,
  records,
  onTeacherSelect,
  onHighlightResource,
  onClearHighlight,
  onClearSlot,
  onSubjectChange,
  onSaved,
  classId,
  academicYear,
}: IntelligentTeacherSuggestionsProps) {
  const { schoolContext, user } = useAuthStore();
  const toast = useToast();
  const queryClient = useQueryClient();

  // State
  const [selectedDay, setSelectedDay] = useState<DayOfWeek | null>(null);
  const [teachersExpanded, setTeachersExpanded] = useState(true);
  const [subjectsExpanded, setSubjectsExpanded] = useState(true);
  const [autoFillDialogOpen, setAutoFillDialogOpen] = useState(false);
  const [autoFillPreview, setAutoFillPreview] = useState<AutoFillPreviewItem[]>([]);

  // Inline form state
  const [formSubjectId, setFormSubjectId] = useState('');
  const [formTeacherId, setFormTeacherId] = useState('');
  const [formRoomId, setFormRoomId] = useState('');

  // Reset form when slot changes
  useMemo(() => {
    setFormSubjectId('');
    setFormTeacherId('');
    setFormRoomId('');
    onSubjectChange?.(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriodSlot?.sectionId, selectedPeriodSlot?.periodId, selectedPeriodSlot?.day]);

  // Get the currently viewed date from the store (for historical/future timetable viewing)
  const temporalDate = useTimetableDesignerStore((state) => state.temporalDate);

  // Convert DayOfWeek to API format (UPPERCASE to match schema constraint)
  const convertDayToApiFormat = (day: DayOfWeek): 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY' => {
    return day; // DayOfWeek is already UPPERCASE — pass through directly
  };

  // Hooks
  const { data: suggestionsData, isLoading: loadingSuggestions } = useTeacherSuggestions(
    selectedPeriodSlot?.sectionId || null,
    selectedPeriodSlot?.periodId || null,
    selectedPeriodSlot?.day ? convertDayToApiFormat(selectedPeriodSlot.day) : null,
    formSubjectId || null
  );

  const autoFillMutation = useAutoFill();

  // Fetch subjects, teachers, rooms for the inline assignment form
  const { data: subjects } = useQuery({
    queryKey: queryKeys.subjects.list({ school_id: schoolContext || '' }),
    queryFn: () => getSubjectsDirect({ school_id: schoolContext || '' }),
    enabled: !!schoolContext && !!selectedPeriodSlot,
  });

  const { data: teachers } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '' }),
    queryFn: () => getTeachersDirect({ school_id: schoolContext || '' }),
    enabled: !!schoolContext && !!selectedPeriodSlot,
  });

  const { data: rooms } = useQuery({
    queryKey: queryKeys.rooms.list({ school_id: schoolContext || '' }),
    queryFn: () => getRoomsDirect({ school_id: schoolContext || '' }),
    enabled: !!schoolContext && !!selectedPeriodSlot,
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!schoolContext || !selectedPeriodSlot) throw new Error('Missing context');
      return createTimetableRecordDirect({
        school_id: schoolContext,
        class_section_id: selectedPeriodSlot.sectionId,
        period_id: selectedPeriodSlot.periodId,
        day_of_week: convertDayToApiFormat(selectedPeriodSlot.day),
        teacher_id: formTeacherId,
        subject_id: formSubjectId,
        room_id: formRoomId || null,
        academic_year: academicYear,
        academic_year_id: null, // caller should pass academicYearId if available
        valid_from: new Date().toISOString().split('T')[0],
        created_by: user?.id,
      });
    },
    onSuccess: () => {
      toast.success('Period assigned successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.timetable.all });
      onSaved?.();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to assign period');
    },
  });

  // Calculate subject allocations
  const subjectAllocations = useMemo<SubjectAllocation[]>(() => {
    const allocMap = new Map<string, SubjectAllocation>();

    records.forEach((record) => {
      if (!record.subject) return;

      const key = record.subject.id;
      if (!allocMap.has(key)) {
        allocMap.set(key, {
          subjectId: record.subject.id,
          subjectName: record.subject.subject_name,
          colorCode: record.subject.color_code || '#e0e0e0',
          periodsToday: 0,
          periodsTotal: 0,
        });
      }

      const allocation = allocMap.get(key)!;
      allocation.periodsTotal += 1;

      // Compare day_of_week from record (API format) with selectedDay (DayOfWeek enum)
      if (selectedDay && record.day_of_week === convertDayToApiFormat(selectedDay)) {
        allocation.periodsToday += 1;
      }
    });

    return Array.from(allocMap.values()).sort((a, b) => 
      b.periodsTotal - a.periodsTotal
    );
  }, [records, selectedDay]);

  // Get suggestions from API response
  const suggestions = useMemo<TeacherSuggestion[]>(() => {
    if (!suggestionsData?.suggestions) return [];
    return suggestionsData.suggestions;
  }, [suggestionsData]);

  // Handle quick assign best match
  const handleQuickAssign = () => {
    if (suggestions.length > 0) {
      setFormTeacherId(suggestions[0].teacher.id);
      onTeacherSelect(suggestions[0].teacher.id);
    }
  };

  // Handle auto-fill
  const handleAutoFill = async () => {
    if (!classId || !academicYear) return;

    try {
      const result = await autoFillMutation.mutateAsync({
        classId,
        sectionIds: sections.map((s) => s.id),
        day: selectedDay ? convertDayToApiFormat(selectedDay) : undefined,
        academicYear,
        date: temporalDate ? temporalDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      });

      // Show preview - Note: AutoFillAssignment doesn't include teacher/subject names
      // We'll need to look them up from the sections data
      const preview: AutoFillPreviewItem[] = result.assignments.map((assignment) => {
        const section = sections.find((s) => s.id === assignment.sectionId);
        return {
          sectionId: assignment.sectionId,
          sectionName: section?.section || '',
          periodId: assignment.periodId,
          day: assignment.day,
          teacherId: assignment.teacherId,
          teacherName: '', // Not available in AutoFillAssignment
          subjectId: assignment.subjectId,
          subjectName: '', // Not available in AutoFillAssignment
        };
      });

      setAutoFillPreview(preview);
      setAutoFillDialogOpen(true);
    } catch (error) {
      console.error('Auto-fill failed:', error);
    }
  };

  if (!editMode) {
    return null;
  }

  return (
    <>
      <SidebarContainer editMode={true} elevation={3}>
        {/* Header */}
        <SidebarHeader>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            Suggestions
          </Typography>
        </SidebarHeader>

        {/* Day Selector */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter by Day</InputLabel>
            <Select
              value={selectedDay || ''}
              label="Filter by Day"
              onChange={(e) => setSelectedDay((e.target.value as DayOfWeek) || null)}
            >
              <MenuItem value="">All Days</MenuItem>
              <MenuItem value="MONDAY">Monday</MenuItem>
              <MenuItem value="TUESDAY">Tuesday</MenuItem>
              <MenuItem value="WEDNESDAY">Wednesday</MenuItem>
              <MenuItem value="THURSDAY">Thursday</MenuItem>
              <MenuItem value="FRIDAY">Friday</MenuItem>
              <MenuItem value="SATURDAY">Saturday</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <ScrollableContent>
          {/* Inline Assignment Form — shown when a slot is selected */}
          {selectedPeriodSlot && (
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'primary.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                  Assign Period
                </Typography>
                <IconButton size="small" onClick={onClearSlot} aria-label="Clear selection">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {selectedPeriodSlot.day.charAt(0) + selectedPeriodSlot.day.slice(1).toLowerCase()}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Subject *</InputLabel>
                  <Select
                    value={formSubjectId}
                    label="Subject *"
                    onChange={(e) => {
                      setFormSubjectId(e.target.value);
                      onSubjectChange?.(e.target.value || null);
                      setFormTeacherId(''); // reset teacher when subject changes
                    }}
                  >
                    {subjects?.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color_code || 'grey.400', flexShrink: 0 }} />
                          {s.subject_name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" required disabled={!formSubjectId}>
                  <InputLabel>Teacher *</InputLabel>
                  <Select
                    value={formTeacherId}
                    label="Teacher *"
                    onChange={(e) => setFormTeacherId(e.target.value)}
                  >
                    {teachers?.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Room (optional)</InputLabel>
                  <Select
                    value={formRoomId}
                    label="Room (optional)"
                    onChange={(e) => setFormRoomId(e.target.value)}
                  >
                    <MenuItem value=""><em>No room</em></MenuItem>
                    {rooms?.map((r) => (
                      <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  startIcon={saveMutation.isPending ? <CircularProgress size={14} /> : <SaveIcon />}
                  disabled={!formSubjectId || !formTeacherId || saveMutation.isPending}
                  onClick={() => saveMutation.mutate()}
                >
                  {saveMutation.isPending ? 'Saving...' : 'Assign'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Teacher Suggestions Section */}
          <Box>
            <SectionHeader onClick={() => setTeachersExpanded(!teachersExpanded)}>
              <Typography variant="subtitle2" fontWeight={600}>
                Teacher Suggestions
              </Typography>
              <IconButton size="small">
                {teachersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </SectionHeader>

            <Collapse in={teachersExpanded}>
              {!selectedPeriodSlot ? (
                <Box sx={{ p: 2 }}>
                  <EmptyState
                    title="No Period Selected"
                    description="Click on an empty period slot to see teacher suggestions"
                  />
                </Box>
              ) : !formSubjectId ? (
                <Box sx={{ p: 2 }}>
                  <EmptyState
                    title="Select a Subject"
                    description="Choose a subject above to load teacher suggestions"
                  />
                </Box>
              ) : loadingSuggestions ? (
                <Box sx={{ p: 2 }}>
                  <LoadingSpinner />
                </Box>
              ) : suggestions.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <EmptyState
                    title="No Suggestions"
                    description="No available teachers found for this period"
                  />
                </Box>
              ) : (
                <>
                  {/* Quick Assign Button */}
                  <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<CheckCircleIcon />}
                      onClick={handleQuickAssign}
                      disabled={suggestions.length === 0}
                    >
                      Quick Assign Best Match
                    </Button>
                  </Box>

                  {/* Teacher List */}
                  <List disablePadding>
                    {suggestions.map((suggestion) => (
                      <TeacherListItem
                        key={suggestion.teacher.id}
                        onClick={() => {
                          setFormTeacherId(suggestion.teacher.id);
                          onTeacherSelect(suggestion.teacher.id);
                          onHighlightResource('teacher', suggestion.teacher.id);
                        }}
                      >
                        {/* Teacher Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Avatar
                            alt={suggestion.teacher.name}
                            sx={{ width: 32, height: 32 }}
                          >
                            {suggestion.teacher.name.charAt(0)}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {suggestion.teacher.name}
                            </Typography>
                          </Box>
                          <RankBadge rank={suggestion.rank}>
                            {suggestion.rank <= 3 && <TrophyIcon fontSize="small" />}
                            #{suggestion.rank}
                          </RankBadge>
                        </Box>

                        {/* Score and Availability */}
                        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                          <ScoreBadge
                            label={`${suggestion.score}%`}
                            size="small"
                            color={suggestion.score >= 80 ? 'success' : suggestion.score >= 60 ? 'warning' : 'default'}
                          />
                          <Chip
                            label={suggestion.availability}
                            size="small"
                            color={suggestion.availability === 'available' ? 'success' : 'default'}
                            icon={<CheckCircleIcon />}
                          />
                        </Box>

                        {/* Reasons */}
                        {suggestion.reasons.length > 0 && (
                          <Box sx={{ width: '100%' }}>
                            {suggestion.reasons.map((reason, idx) => (
                              <Typography key={idx} variant="caption" color="text.secondary" display="block">
                                • {reason}
                              </Typography>
                            ))}
                          </Box>
                        )}

                        {/* Workload Indicator */}
                        <WorkloadBar>
                          <Tooltip
                            title={`${suggestion.workload.currentPeriods}/${suggestion.workload.maxPeriods} periods (${Math.round(suggestion.workload.percentage)}%)`}
                            arrow
                          >
                            <Box sx={{ flex: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={suggestion.workload.percentage}
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor:
                                      suggestion.workload.percentage < 50
                                        ? 'success.main'
                                        : suggestion.workload.percentage < 80
                                        ? 'warning.main'
                                        : 'error.main',
                                  },
                                }}
                              />
                            </Box>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                            {suggestion.workload.currentPeriods}/{suggestion.workload.maxPeriods}
                          </Typography>
                        </WorkloadBar>

                        {/* Qualification Chips */}
                        {suggestion.qualifications && suggestion.qualifications.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                            {suggestion.qualifications.map((subject) => (
                              <Chip
                                key={subject.id}
                                label={subject.subject_name}
                                size="small"
                                sx={{
                                  backgroundColor: subject.color_code || '#e0e0e0',
                                  color: '#000',
                                  fontSize: '0.7rem',
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </TeacherListItem>
                    ))}
                  </List>
                </>
              )}
            </Collapse>
          </Box>

          <Divider />

          {/* Subject Allocation Section */}
          <Box>
            <SectionHeader onClick={() => setSubjectsExpanded(!subjectsExpanded)}>
              <Typography variant="subtitle2" fontWeight={600}>
                Subject Allocation
              </Typography>
              <IconButton size="small">
                {subjectsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </SectionHeader>

            <Collapse in={subjectsExpanded}>
              {subjectAllocations.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <EmptyState
                    title="No Subjects"
                    description="No subjects have been assigned yet"
                  />
                </Box>
              ) : (
                <List disablePadding>
                  {subjectAllocations.map((allocation) => (
                    <SubjectListItem
                      key={allocation.subjectId}
                      onClick={() => onHighlightResource('subject', allocation.subjectId)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: 1,
                            backgroundColor: allocation.colorCode,
                          }}
                        />
                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                          {allocation.subjectName}
                        </Typography>
                        <IconButton size="small" onClick={(e) => {
                          e.stopPropagation();
                          onHighlightResource('subject', allocation.subjectId);
                        }}>
                          <HighlightIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                        <Typography variant="caption" color="text.secondary">
                          Today: {allocation.periodsToday} periods
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total: {allocation.periodsTotal} periods
                        </Typography>
                      </Box>
                    </SubjectListItem>
                  ))}
                </List>
              )}
            </Collapse>
          </Box>
        </ScrollableContent>

        {/* Footer with Auto-Fill */}
        <Divider />
        <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<AutoFixIcon />}
            onClick={handleAutoFill}
            disabled={autoFillMutation.isPending}
          >
            {autoFillMutation.isPending ? 'Processing...' : 'Auto-Fill Empty Slots'}
          </Button>
        </Box>
      </SidebarContainer>

      {/* Auto-Fill Preview Dialog */}
      <Dialog
        open={autoFillDialogOpen}
        onClose={() => setAutoFillDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Auto-Fill Preview</DialogTitle>
        <DialogContent>
          {autoFillPreview.length === 0 ? (
            <Alert severity="info">No empty slots to fill</Alert>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                {autoFillPreview.length} period{autoFillPreview.length !== 1 ? 's' : ''} will be assigned
              </Alert>
              <List>
                {autoFillPreview.map((item, idx) => (
                  <ListItem key={idx} divider>
                    <ListItemText
                      primary={`${item.sectionName} - ${item.day} - Period ${item.periodId}`}
                      secondary={`${item.subjectName} with ${item.teacherName}`}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoFillDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setAutoFillDialogOpen(false);
              // Assignments are already created by the API
            }}
            disabled={autoFillPreview.length === 0}
          >
            Accept All
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
